// TanStack Query hooks for the four FE-SCRM-058 reports + the
// competitor side panel's closed-opportunities feed. Each card mounts
// its hook independently so a slow query on one report doesn't block
// the other tiles (per spec acceptance criterion).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  fetchClosedOpportunities,
  fetchCompetitors,
  fetchLossAnalysis,
  fetchReferralSources,
  fetchVelocityReport,
} from "./api";
import type {
  CompetitorListResponse,
  LossAnalysisResponse,
  OpportunityListResponse,
  ReferralSourcesResponse,
  ReportPeriod,
  VelocityResponse,
} from "./types";


export const VELOCITY_QUERY_KEY = "sales-velocity-report" as const;
export const REFERRAL_QUERY_KEY = "sales-referral-sources" as const;
export const LOSS_ANALYSIS_QUERY_KEY = "sales-loss-analysis" as const;
export const COMPETITORS_QUERY_KEY = "sales-competitors" as const;
export const CLOSED_OPPS_QUERY_KEY = "sales-closed-opportunities" as const;


export function useVelocityReportQuery(
  windowDays: number,
): UseQueryResult<VelocityResponse> {
  return useQuery({
    queryKey: [VELOCITY_QUERY_KEY, windowDays],
    queryFn: ({ signal }) =>
      fetchVelocityReport({ signal, windowDays }),
  });
}


export function useReferralSourcesQuery(
  period: ReportPeriod,
): UseQueryResult<ReferralSourcesResponse> {
  return useQuery({
    queryKey: [REFERRAL_QUERY_KEY, period],
    queryFn: ({ signal }) =>
      fetchReferralSources({ signal, period }),
  });
}


export function useLossAnalysisQuery(
  period: ReportPeriod,
): UseQueryResult<LossAnalysisResponse> {
  return useQuery({
    queryKey: [LOSS_ANALYSIS_QUERY_KEY, period],
    queryFn: ({ signal }) =>
      fetchLossAnalysis({ signal, period }),
  });
}


export function useCompetitorsQuery(
  period: ReportPeriod,
): UseQueryResult<CompetitorListResponse> {
  return useQuery({
    queryKey: [COMPETITORS_QUERY_KEY, period],
    queryFn: ({ signal }) => fetchCompetitors({ signal, period }),
  });
}


/** Closed-opportunities (won + lost) feed used by the competitor
 * side panel for its client-side normalised-firm-name match.
 * ``enabled`` defaults to ``false`` — the panel flips it on the
 * first time a competitor row is opened so we don't fan out twice
 * just to render the headline table. */
export function useClosedOpportunitiesQuery(
  enabled: boolean,
): UseQueryResult<OpportunityListResponse> {
  return useQuery({
    queryKey: [CLOSED_OPPS_QUERY_KEY],
    queryFn: ({ signal }) => fetchClosedOpportunities({ signal }),
    enabled,
  });
}
